import { useMedplum, useMedplumProfile } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';
import { useCallback, useMemo } from 'react';

/**
 * Interface for conversation tracking results returned from the tracking bot
 */
interface ConversationTrackingResult {
  conversationsUsed: number;
  conversationsAllowed: number | null;
  remainingConversations: number | null;
  hasReachedLimit: boolean;
  isLimitEnforced: boolean;
}

/**
 * A React hook that provides subscription status and feature access information
 * for the FeelHeard application based on the user's subscription tier.
 * 
 * Features included:
 * - Current subscription tier and status
 * - Voice conversation access
 * - Conversation count limits
 * - History retention
 * - Subscription management functions
 */
export function useSubscriptionFeatures() {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Patient;
  
  // Get subscription status directly from patient profile
  const subscriptionStatus = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-status'
  )?.valueString || 'free';
  
  // Get subscription tier from patient profile
  const subscriptionTier = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-tier'
  )?.valueString as 'free' | 'basic' | 'premium' | undefined || 'free';
  
  // Get billing cycle (monthly/annual)
  const billingCycle = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-billing-cycle'
  )?.valueString as 'monthly' | 'annual' | undefined;
  
  // Get trial end date if applicable
  const trialEndDate = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/trial-end-date'
  )?.valueDateTime;
  
  // Get conversation count
  const conversationCount = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-conversations-used'
  )?.valueInteger || 0;
  
  // Get conversation limit
  const conversationLimit = profile?.extension?.find(
    e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-conversations-allowed'
  )?.valueInteger;
  
  // Compute derived state
  // Subscription status flags
  const isTrialActive = subscriptionStatus === 'trial';
  const isFree = subscriptionStatus === 'free' || subscriptionStatus === 'canceled';
  const isActive = subscriptionStatus === 'active';
  
  // Tier flags - using original tier names in code for backward compatibility
  // 'premium' means Voice Connect
  // 'basic' means Text Companion
  const isPremium = subscriptionTier === 'premium';
  const isBasic = subscriptionTier === 'basic';
  
  console.log('[useSubscriptionFeatures] Raw Profile Values:', { 
    subscriptionStatus,
    subscriptionTier,
    conversationCount,
    conversationLimit,
    isTrialActive
  });
  
  // Calculate remaining days in trial
  const trialDaysRemaining = useMemo(() => {
    if (!trialEndDate) return 0;
    const now = new Date();
    const endDate = new Date(trialEndDate);
    const diffTime = endDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  }, [trialEndDate]);
  
  // Determine feature flags - no more conversation limits
  const canUseVoice = subscriptionStatus === 'trial' || subscriptionTier === 'premium';
  const hasUnlimitedHistory = subscriptionStatus === 'trial' || subscriptionTier === 'premium';
  const canStartNewConversation = 
    subscriptionStatus === 'trial' || 
    ['premium', 'basic'].includes(subscriptionTier as string);
  
  console.log('[useSubscriptionFeatures] Calculated Flags:', { 
    canUseVoice,
    hasUnlimitedHistory,
    canStartNewConversation,
    subscriptionTier // Also log the tier being returned
  });
  
  // Conversation tracking functions removed as per conversation limits removal plan
  
  /**
   * Helper to get price ID based on tier and billing cycle
   */
  const getPriceId = useCallback((tier: 'basic' | 'premium', annual: boolean): string => {
    // Determine if we're in test or prod mode
    const stripeMode = import.meta.env.VITE_STRIPE_MODE || 'TEST';
    console.log(`Using Stripe mode: ${stripeMode}`);
    
    // Use the correct price IDs based on mode
    const priceMapping = stripeMode === 'TEST' ? 
      {
        // TEST mode price IDs - Special pricing
        'basic-monthly': 'price_1RJqxuIfLgrjtRiqsg9bnkJf',  // $9.99/month (Special)
        'basic-annual': 'price_1RJr1xIfLgrjtRiqrW30KrjC',   // $95.88/year (Special)
        'premium-monthly': 'price_1RJqzPIfLgrjtRiq32aqFawQ', // $19.99/month (Special)
        'premium-annual': 'price_1RJr5IIfLgrjtRiq06WKvnNC'   // $191.88/year (Special)
        
        // Original Regular Pricing (commented out for reference)
        // 'basic-monthly': 'price_1RI3nMIfLgrjtRiq9tXej1QM',  // $14.99/month
        // 'basic-annual': 'price_1RI3nMIfLgrjtRiqPdc1FNWa',   // $143.88/year
        // 'premium-monthly': 'price_1RI3nMIfLgrjtRiqsJ2NNnSf', // $29.99/month
        // 'premium-annual': 'price_1RI3nMIfLgrjtRiqVPTBy2T2'   // $287.88/year
      } : 
      {
        // PROD mode price IDs - Special pricing
        'basic-monthly': 'price_1RJr8pIfLgrjtRiqN6l6GzdX',  // $9.99/month (Special)
        'basic-annual': 'price_1RJr9WIfLgrjtRiqR4vVZpCG',   // $95.88/year (Special)
        'premium-monthly': 'price_1RJrAFIfLgrjtRiqgQoYOpjm', // $19.99/month (Special)
        'premium-annual': 'price_1RJrBqIfLgrjtRiqtmJbnB6S'   // $191.88/year (Special)
        
        // Original Regular Pricing (commented out for reference)
        // 'basic-monthly': 'price_1RI4BPIfLgrjtRiqy0RIuD0S',  // $14.99/month
        // 'basic-annual': 'price_1RI4BPIfLgrjtRiqzs5PZvEc',   // $143.88/year
        // 'premium-monthly': 'price_1RI4BPIfLgrjtRiqGM6UJb1S', // $29.99/month
        // 'premium-annual': 'price_1RI4BPIfLgrjtRiq1faFDCth'   // $287.88/year
      };
    
    const key = `${tier}-${annual ? 'annual' : 'monthly'}`;
    return priceMapping[key as keyof typeof priceMapping];
  }, []);
  
  /**
   * Creates or updates a subscription
   * @param tier The subscription tier to subscribe to
   * @param annual Whether to bill annually or monthly
   * @returns Promise that resolves to client secret for payment
   */
  const createSubscription = useCallback(async (
    tier: 'basic' | 'premium', 
    annual: boolean
  ): Promise<{ clientSecret: string | null; status: string }> => {
    if (!profile?.id) {
      throw new Error('User profile not found');
    }
    
    const patientName = profile.name?.[0]?.text || 
      [profile.name?.[0]?.given?.join(' '), profile.name?.[0]?.family].filter(Boolean).join(' ');
    
    const patientEmail = profile.telecom?.find(t => t.system === 'email')?.value;
    
    // Determine action based on subscription status
    const action = subscriptionStatus === 'active' ? 'upgrade' : 'create';
    
    // Get price ID for the selected tier and billing cycle
    const priceId = getPriceId(tier, annual);
    
    console.log('Starting subscription process:', { 
      tier, 
      annual, 
      priceId, 
      action, 
      patientId: profile.id,
      hasEmail: !!patientEmail
    });
    
    // Execute the subscription bot
    try {
      const botParams = {
        resourceType: 'Parameters',
        parameter: [
          {
            name: 'resourceType',
            valueString: 'Patient'
          },
          {
            name: 'patientId',
            valueString: profile.id
          },
          {
            name: 'priceId',
            valueString: priceId
          },
          {
            name: 'action',
            valueString: action
          },
          {
            name: 'productType',
            valueString: 'FEELHEARD'
          },
          {
            name: 'customerName',
            valueString: patientName
          },
          ...(patientEmail ? [{
            name: 'customerEmail',
            valueString: patientEmail
          }] : [])
        ]
      };
      
      console.log('Calling create-subscription bot with params:', JSON.stringify(botParams, null, 2));
      
      const response = await medplum.executeBot(
        {
          system: 'https://progressnotes.app',
          value: 'create-subscription'
        },
        botParams
      );
      
      console.log('Bot response received:', JSON.stringify(response, null, 2));
      
      // Parse response parameters
      const clientSecret = response.parameter?.find(
        (p: { name: string; valueString?: string }) => p.name === 'clientSecret'
      )?.valueString || null;
      
      const status = response.parameter?.find(
        (p: { name: string; valueString?: string }) => p.name === 'status'
      )?.valueString || 'unknown';
      
      // Check if the response is an OperationOutcome (error)
      if (response.resourceType === 'OperationOutcome' && response.issue && response.issue.length > 0) {
        console.error('Bot returned an error:', response.issue.map((i: any) => i.details?.text || i.diagnostics || 'Unknown error').join(', '));
      }
      
      console.log('Processed bot response:', { 
        hasClientSecret: !!clientSecret, 
        clientSecret: clientSecret ? `${clientSecret.substring(0, 10)}...` : null,
        status,
        isError: response.resourceType === 'OperationOutcome'
      });
      
      return { clientSecret, status };
    } catch (error) {
      console.error('Error calling create-subscription bot:', error);
      throw error;
    }
  }, [medplum, profile, subscriptionStatus, getPriceId]);
  
  /**
   * Cancels the current subscription
   * @returns Promise that resolves to the cancellation status
   */
  const cancelSubscription = useCallback(async (): Promise<{ status: string }> => {
    if (!profile?.id) {
      throw new Error('User profile not found');
    }
    
    const response = await medplum.executeBot(
      {
        system: 'https://progressnotes.app',
        value: 'create-subscription'
      },
      {
        resourceType: 'Parameters',
        parameter: [
          {
            name: 'resourceType',
            valueString: 'Patient'
          },
          {
            name: 'patientId',
            valueString: profile.id
          },
          {
            name: 'action',
            valueString: 'cancel'
          },
          {
            name: 'productType',
            valueString: 'FEELHEARD'
          }
        ]
      }
    );
    
    const status = response.parameter?.find(
      (p: { name: string; valueString?: string }) => p.name === 'status'
    )?.valueString || 'unknown';
    
    return { status };
  }, [medplum, profile?.id]);
  
  return {
    // Subscription info
    subscriptionStatus,
    subscriptionTier,
    billingCycle,
    isTrialActive,
    isFree,
    isActive,
    isPremium,
    isBasic,
    trialEndDate,
    trialDaysRemaining,
    
    // Usage and limits - kept for backward compatibility, but no longer actively used
    conversationCount,
    conversationLimit,
    
    // Feature flags
    canUseVoice,
    hasUnlimitedHistory,
    canStartNewConversation,
    
    // Actions
    createSubscription,
    cancelSubscription,
    getPriceId,
    
    // Raw data for advanced usage
    profile
  };
}